import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { useLanguage } from '../components/Layout/LanguageContext';
import { blogPosts } from '../data/blogPosts';

const BlogSection = styled.section`
  padding: 6rem 2rem;
  background: var(--background-color);
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 600;
  margin-bottom: 2rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: var(--background-light);
    transform: translateX(-4px);
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const SectionTitle = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: var(--text-secondary);
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
`;

const PostGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 2.5rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const PostCard = styled(motion(Link))`
  display: flex;
  flex-direction: column;
  background: var(--background-light);
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 12px 40px rgba(0, 51, 102, 0.1);
  border: 1px solid var(--background-dark);
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 60px rgba(0, 51, 102, 0.15);
    border-color: var(--accent-color);
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 12px;
  margin-bottom: 1.25rem;
  display: block;
`;

const PostMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const CategoryBadge = styled.span`
  background: linear-gradient(135deg, var(--primary-color), var(--accent-color));
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.3rem 0.8rem;
  border-radius: 999px;
`;

const PostDate = styled.span`
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

const PostTitle = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--primary-color);
  margin: 0 0 0.75rem;
  line-height: 1.35;
`;

const PostExcerpt = styled.p`
  font-size: 0.95rem;
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0 0 1.5rem;
  flex: 1;
`;

const ReadMore = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-color);
  font-weight: 600;
  font-size: 0.9rem;
`;

const blogContent = {
  de: {
    title: 'Blog',
    subtitle:
      'Fachliche Einblicke zu Power Apps, Power BI, Power Automate sowie KI und Daten – aus der Praxis der südlichsten IT-Beratung Deutschlands.',
    backButton: 'Zurück zur Startseite',
    readMore: 'Weiterlesen',
  },
  en: {
    title: 'Blog',
    subtitle:
      'Professional insights on Power Apps, Power BI, Power Automate as well as AI and data – from the practice of the southernmost IT consultancy in Germany.',
    backButton: 'Back to Homepage',
    readMore: 'Read more',
  },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const Blog: React.FC = () => {
  const { language } = useLanguage();
  const t = blogContent[language];
  const locale = language === 'de' ? 'de-DE' : 'en-GB';

  const sortedPosts = [...blogPosts].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  return (
    <BlogSection>
      <Container>
        <BackButton to="/">
          <FaArrowLeft />
          {t.backButton}
        </BackButton>

        <SectionHeader>
          <SectionTitle>{t.title}</SectionTitle>
          <SectionSubtitle>{t.subtitle}</SectionSubtitle>
        </SectionHeader>

        <PostGrid
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {sortedPosts.map((post) => {
            const c = post[language];
            return (
              <PostCard
                key={post.slug}
                to={`/blog/${post.slug}`}
                variants={cardVariants}
              >
                {post.image && <CardImage src={post.image} alt={c.title} />}
                <PostMeta>
                  <CategoryBadge>{post.category[language]}</CategoryBadge>
                  <PostDate>
                    {new Date(post.date).toLocaleDateString(locale, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </PostDate>
                </PostMeta>
                <PostTitle>{c.title}</PostTitle>
                <PostExcerpt>{c.excerpt}</PostExcerpt>
                <ReadMore>
                  {t.readMore}
                  <FaArrowRight />
                </ReadMore>
              </PostCard>
            );
          })}
        </PostGrid>
      </Container>
    </BlogSection>
  );
};

export default Blog;
